import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useCommonTranslation } from "@tablemint/i18n";
import { supportedLngs } from "@tablemint/i18n";
import { useAuth, useUser, UserButton } from "@clerk/clerk-react";
import { fadeIn, fadeInUp, slideInRight } from "../../utils/animation";

const Header = () => {
  const { t, i18n } = useCommonTranslation();
  const { isSignedIn, isLoaded } = useAuth();
  const { user } = useUser();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const currentLang = i18n.language || "en";

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    if (isLangMenuOpen) setIsLangMenuOpen(false);
  };

  const toggleLangMenu = () => {
    setIsLangMenuOpen(!isLangMenuOpen);
    if (isDropdownOpen) setIsDropdownOpen(false);
  };

  const changeLang = (lng: string) => {
    i18n.changeLanguage(lng);
    setIsLangMenuOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (isDropdownOpen) setIsDropdownOpen(false);
      if (isLangMenuOpen) setIsLangMenuOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen, isLangMenuOpen]);

  const dashboardLinks = [
    { href: "/dashboard", label: t("navigation.dashboard") },
    { href: "/widget-configurator", label: t("navigation.widgetConfigurator") },
    { href: "/reservations", label: t("navigation.reservations") },
    { href: "/settings", label: t("navigation.settings") },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-40 bg-white dark:bg-background-dark shadow-sm">
      <div className="safe-padded max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4 md:justify-start md:space-x-10">
          {/* Logo */}
          <div className="flex justify-start lg:w-0 lg:flex-1">
            <a href="/" className="flex items-center">
              <span className="text-primary text-xl font-bold">
                {t("app.name")}
              </span>
            </a>
          </div>

          {/* Mobile menu button */}
          <div className="-mr-2 -my-2 md:hidden">
            <button
              onClick={toggleMenu}
              className="touch-target inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 focus:outline-none"
              aria-expanded={isMenuOpen}
            >
              <span className="sr-only">
                {isMenuOpen ? "Close menu" : "Open menu"}
              </span>
              <svg
                className="h-6 w-6"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                {isMenuOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </button>
          </div>

          {/* Desktop navigation */}
          <nav className="hidden md:flex space-x-10">
            <a
              href="/"
              className="text-base font-medium text-gray-500 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
            >
              {t("navigation.home")}
            </a>
            <a
              href="/features"
              className="text-base font-medium text-gray-500 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
            >
              {t("navigation.features")}
            </a>
            <a
              href="/pricing"
              className="text-base font-medium text-gray-500 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
            >
              {t("navigation.pricing")}
            </a>
            <a
              href="/about"
              className="text-base font-medium text-gray-500 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
            >
              {t("navigation.about")}
            </a>
          </nav>

          {/* Desktop right buttons */}
          <div className="hidden md:flex items-center justify-end md:flex-1 lg:w-0 space-x-4">
            {/* Language switcher */}
            <div className="relative">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleLangMenu();
                }}
                className="flex items-center text-gray-500 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
              >
                <span className="mr-1">{currentLang.toUpperCase()}</span>
                <svg
                  className="h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              <AnimatePresence>
                {isLangMenuOpen && (
                  <motion.div
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                    variants={fadeInUp}
                    className="absolute right-0 mt-2 w-28 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 focus:outline-none z-50"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="py-1">
                      {supportedLngs.map((lng) => (
                        <button
                          key={lng}
                          onClick={() => changeLang(lng)}
                          className={`w-full text-left px-4 py-2 text-sm ${
                            currentLang === lng
                              ? "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                              : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                          }`}
                        >
                          {lng.toUpperCase()}
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {isLoaded && isSignedIn ? (
              <>
                <div className="relative">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsDropdownOpen(!isDropdownOpen);
                      if (isLangMenuOpen) setIsLangMenuOpen(false);
                    }}
                    className="flex items-center text-gray-500 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
                  >
                    <span className="mr-1">{t("navigation.dashboard")}</span>
                    <svg
                      className={`h-4 w-4 transform transition-transform duration-200 ${
                        isDropdownOpen ? "rotate-180" : ""
                      }`}
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>

                  <AnimatePresence>
                    {isDropdownOpen && (
                      <motion.div
                        initial="hidden"
                        animate="visible"
                        exit="hidden"
                        variants={fadeInUp}
                        className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 focus:outline-none z-50"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="py-1">
                          {dashboardLinks.map((link) => (
                            <a
                              key={link.href}
                              href={link.href}
                              onClick={() => setIsDropdownOpen(false)}
                              className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-150"
                            >
                              {link.label}
                            </a>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                <UserButton afterSignOutUrl="/" />
              </>
            ) : (
              <>
                <a
                  href="/sign-in"
                  className="text-base font-medium text-gray-500 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
                >
                  {t("navigation.login")}
                </a>
                <a
                  href="/sign-up"
                  className="touch-target ml-8 inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-primary hover:bg-primary-hover transform transition-transform duration-200 hover:scale-105"
                >
                  {t("navigation.signup")}
                </a>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={slideInRight}
            className="absolute top-0 inset-x-0 p-2 transition transform origin-top-right md:hidden"
          >
            <div className="rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 bg-white dark:bg-background-dark divide-y-2 divide-gray-50 dark:divide-gray-800">
              <div className="pt-5 pb-6 px-5">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-primary text-xl font-bold">
                      {t("app.name")}
                    </span>
                  </div>
                  <div className="-mr-2">
                    <button
                      onClick={toggleMenu}
                      className="touch-target bg-white dark:bg-background-dark rounded-md p-2 inline-flex items-center justify-center text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
                    >
                      <span className="sr-only">Close menu</span>
                      <svg
                        className="h-6 w-6"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="mt-6">
                  <nav className="grid gap-y-8">
                    <a
                      href="/"
                      className="touch-target -m-3 p-3 flex items-center rounded-md hover:bg-gray-50 dark:hover:bg-gray-800"
                    >
                      <span className="ml-3 text-base font-medium text-gray-900 dark:text-gray-100">
                        {t("navigation.home")}
                      </span>
                    </a>
                    <a
                      href="/features"
                      className="touch-target -m-3 p-3 flex items-center rounded-md hover:bg-gray-50 dark:hover:bg-gray-800"
                    >
                      <span className="ml-3 text-base font-medium text-gray-900 dark:text-gray-100">
                        {t("navigation.features")}
                      </span>
                    </a>
                    <a
                      href="/pricing"
                      className="touch-target -m-3 p-3 flex items-center rounded-md hover:bg-gray-50 dark:hover:bg-gray-800"
                    >
                      <span className="ml-3 text-base font-medium text-gray-900 dark:text-gray-100">
                        {t("navigation.pricing")}
                      </span>
                    </a>
                    <a
                      href="/about"
                      className="touch-target -m-3 p-3 flex items-center rounded-md hover:bg-gray-50 dark:hover:bg-gray-800"
                    >
                      <span className="ml-3 text-base font-medium text-gray-900 dark:text-gray-100">
                        {t("navigation.about")}
                      </span>
                    </a>

                    {isLoaded && isSignedIn ? (
                      <>
                        {dashboardLinks.map((link) => (
                          <a
                            key={link.href}
                            href={link.href}
                            className="touch-target -m-3 p-3 flex items-center rounded-md hover:bg-gray-50 dark:hover:bg-gray-800"
                            onClick={() => setIsMenuOpen(false)}
                          >
                            <span className="ml-3 text-base font-medium text-gray-900 dark:text-gray-100">
                              {link.label}
                            </span>
                          </a>
                        ))}
                      </>
                    ) : (
                      <>
                        <a
                          href="/sign-in"
                          className="touch-target -m-3 p-3 flex items-center rounded-md hover:bg-gray-50 dark:hover:bg-gray-800"
                        >
                          <span className="ml-3 text-base font-medium text-gray-900 dark:text-gray-100">
                            {t("navigation.login")}
                          </span>
                        </a>
                        <a
                          href="/sign-up"
                          className="touch-target -m-3 p-3 flex items-center rounded-md hover:bg-gray-50 dark:hover:bg-gray-800"
                        >
                          <span className="ml-3 text-base font-medium text-gray-900 dark:text-gray-100">
                            {t("navigation.signup")}
                          </span>
                        </a>
                      </>
                    )}
                  </nav>
                </div>
              </div>

              {isLoaded && isSignedIn && (
                <div className="py-6 px-5 flex items-center">
                  <div className="mr-2">
                    <UserButton afterSignOutUrl="/" />
                  </div>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {user?.firstName || user?.username}
                  </span>
                </div>
              )}

              <div className="py-6 px-5">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {t("app.language")}
                  </span>
                  <div className="flex space-x-3">
                    {supportedLngs.map((lng) => (
                      <button
                        key={lng}
                        onClick={() => changeLang(lng)}
                        className={`px-2 py-1 text-sm rounded ${
                          currentLang === lng
                            ? "bg-primary text-white"
                            : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                        }`}
                      >
                        {lng.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
